import React, { Fragment, useEffect, useState } from 'react';
import { Typography, Box, Grid } from '@material-ui/core';
import { useTheme } from '@material-ui/core/styles';
import { useParams, navigate } from '@reach/router';
import colors from '../../../themes/main-colors';
import useContentMgmt from '../../../hooks/useContentMgmt';
import microcopyGroupIds from '../../pge-plus-common/microcopyGroupIds';
import { useInstallerJobDetailsQuery } from '../../../hooks/pge-plus/useInstallerJobDetailsQuery';
import InstallerPageHeader from '../../pge-plus-common/InstallerPageHeader';
import InstallerDateAndTime from '../installer-common/InstallerDateAndTime';
import {
  subText1,
  boldText,
  subTextWithoutMargin,
} from '../installer-common/quoteCss';
import QuoteCustomerInfo from '../installer-common/quote-customer-info';
import {
  JobStatus,
  LineItemCost,
  LineItemProduct,
  LineItemRebate,
} from '../../../__generated__/pgeplus-types';
import Backdrop from '../../backdrop';
import JobInstallationCostDisplay from '../installer-common/jobInstallationCostDisplay';
import useQuote from '../confirm-cost/useQuote';
import { usePrintOrDownload } from '../../../hooks/usePrintOrDownload';
import useFileUploadApi from '../../../hooks/pge-plus/useFileUploadApi';
import { PgeStoreFileUrlResponse } from '../../../__generated__/pge-types';
import InstallerButton from '../../pge-plus-common/InstallerButton';
import ROUTES from '../../../routes';
import SaveAltIcon from '@material-ui/icons/SaveAlt';
import { LINE_ITEM_KEY } from '../quote.types';
import { isPdfFile } from '../../pge-plus-common/FileUploader';
import { toCurrencyString } from '../../../util/format';

const JobCompleted = () => {
  const { content, getProductList } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_INSTALLER_CONFIRMATION_DETAILS_AND_COST,
  );
  const params = useParams();
  const { loading, data } = useInstallerJobDetailsQuery(params?.jobId || '');
  const [showSerialNumber, setShowSerialNumber] = useState(false);
  const [imageList, setImageList] = useState<PgeStoreFileUrlResponse[]>();
  const jobData = data?.getJob.job;
  const applicationData = data?.getJob.application;
  const theme = useTheme();
  const totalInstallationCss = {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: theme.spacing(2),
  };
  const {
    totalCost,
    totalInstallation,
    costBeforeRebate,
    calculateTotalAmount,
  } = useQuote(content);
  const { handleDownloadWithImage } = usePrintOrDownload({
    containerId: 'print_section',
    minHeight: 100,
    filename: `pge_plus_job_${data?.getJob?.job?.id}.pdf`,
  });
  const lineItemRebateList =
    data?.getJob &&
    (data?.getJob.job.jobDetails?.data?.lineItems?.filter(
      (item: any) => item.__typename === 'LineItemRebate',
    ) as LineItemRebate[]);

  const jobImageList = jobData && jobData?.jobDetails?.data.images;

  useEffect(() => {
    if (data?.getJob) {
      const lineItemCostList = data?.getJob.job.jobDetails?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemCost',
      ) as LineItemCost[];
      const lineItemProductList = data?.getJob.job.jobDetails?.data?.lineItems?.filter(
        (item: any) => item.__typename === 'LineItemProduct',
      ) as LineItemProduct[];
      const chargerModalName = lineItemProductList?.filter(
        item => item.key === 'EV_CHARGER_MODEL',
      )[0].name;
      if (!chargerModalName.toLowerCase().includes('juicebox')) {
        setShowSerialNumber && setShowSerialNumber(true);
      } else {
        setShowSerialNumber && setShowSerialNumber(false);
      }
      calculateTotalAmount(false, {
        totalInstallationMaterial:
          (lineItemCostList &&
            lineItemCostList.filter(
              item => item.key === 'INSTALLATION_MATERIALS',
            )[0].amount) ||
          0,
        chargerCost:
          lineItemProductList &&
          lineItemProductList?.filter(
            item => item.key === 'EV_CHARGER_MODEL',
          )[0].amount,
        meterBase:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'METER_BASE')[0].amount,
        weatherHead:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'WEATHER_HEAD')[0]
            .amount,
        electricBreaker:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'ELECTRICAL_BREAKER')[0]
            .amount,
        miscMaterial:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'MISC_MATERIALS')[0]
            .amount,
        labor:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'LABOR')[0].amount,
        material:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'MATERIALS')[0].amount,
        permitCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'PERMITS')[0].amount,
        travelCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'TRAVEL_TIME')[0].amount,
        feesCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'FEES')[0].amount,
        miscellaneousWorkCost:
          lineItemCostList &&
          lineItemCostList.filter(item => item.key === 'MISCELLANEOUS_WORK')[0]
            .amount,
      });
    }
  }, [jobData]);

  const hr = {
    border: '1px solid #D7D7D7',
    marginTop: theme.spacing(3),
    marginBottom: theme.spacing(3),
  };

  const serialNumberURL =
    jobImageList?.find(imgItem => imgItem.key === 'IMAGE_CHARGER_SERIAL_NO')
      ?.imageUrl || '';
  const chargerInstallationURL =
    jobImageList?.find(imgItem => imgItem.key === 'IMAGE_CHARGER_INSTALLATION')
      ?.imageUrl || '';
  const customerInvoiceURL =
    jobImageList?.find(imgItem => imgItem.key === 'IMAGE_CUSTOMER_INVOICE')
      ?.imageUrl || '';

  return (
    <Fragment>
      {loading && <Backdrop forceOpen />}
      <Box
        id="print_section"
        display="flex"
        flexDirection="column"
        mt={1}
        css={{
          maxWidth: '706px',
          justifyContent: 'center',
          margin: '0 auto',
          marginTop: 16,
          marginBottom: 100,
        }}
      >
        <Grid container>
          <Grid item xs={12}>
            <InstallerPageHeader
              pageHeaderInfo={{
                pageTitle:
                  jobData?.status === JobStatus.ServiceCompleted
                    ? content.get('JOB_COMPLETED_AWAITING_REIMBURSEMENT')
                    : content.get('JOB_COMPLETED'),
                pageSubTitle: '',
              }}
              jobData={data?.getJob!}
              content={content}
            />
            <InstallerDateAndTime
              installationDateTime={jobData?.serviceTime}
              parentPage={'job-completed'}
              content={content}
              title={content.get('REUSABLE_INSTALLATION_DATE_AND_TIME')}
            />
            <hr css={{ ...hr }} />
            <QuoteCustomerInfo
              content={content}
              jobData={jobData?.jobLocation!}
              applicationNo={applicationData?.applicationNo!}
            />
            <hr css={{ ...hr }} />
            <JobInstallationCostDisplay
              content={content}
              jobData={jobData?.jobDetails!}
              applicationData={applicationData!}
              totalInstallationCost={totalInstallation}
            />
            <hr css={{ ...hr }} />
            <Grid data-print-exclude>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <Typography css={{ ...subText1 }}>
                  {content.get('UPLOADED_PHOTOS')}
                </Typography>
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <Typography css={{ ...boldText }}>
                  {showSerialNumber
                    ? content.get('CHARGER_SERIAL_NUMBER')
                    : content.get('JUICE_BOX_ID_NUMBER')}
                </Typography>
                <Typography css={{ ...boldText }}>
                  {data &&
                    data?.getJob.job.jobDetails?.data &&
                    (data?.getJob.job.jobDetails?.data?.lineItems?.filter(
                      item => item.key === LINE_ITEM_KEY.EV_CHARGER_MODEL,
                    )[0] as any).productMeta.serialNumber}
                </Typography>
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                {isPdfFile(serialNumberURL) ? (
                  <iframe
                    src={serialNumberURL}
                    style={{ width: '100%', height: '475px' }}
                  />
                ) : (
                  <img
                    src={serialNumberURL}
                    css={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </Box>

              <Box css={{ marginBottom: theme.spacing(3) }}>
                <Typography css={{ ...boldText }}>
                  {content.get('COMPLETED_CHARGER_INSTALLATION')}
                </Typography>
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                {isPdfFile(chargerInstallationURL) ? (
                  <iframe
                    src={chargerInstallationURL}
                    style={{ width: '100%', height: '475px' }}
                  />
                ) : (
                  <img
                    src={chargerInstallationURL}
                    css={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                <Typography css={{ ...boldText }}>
                  {content.get('COPY_OF_CUSTOMERS_INVOICE')}
                </Typography>
              </Box>
              <Box css={{ marginBottom: theme.spacing(3) }}>
                {isPdfFile(customerInvoiceURL) ? (
                  <iframe
                    src={customerInvoiceURL}
                    style={{ width: '100%', height: '475px' }}
                  />
                ) : (
                  <img
                    src={customerInvoiceURL}
                    css={{ width: '100%', height: '100%', objectFit: 'cover' }}
                  />
                )}
              </Box>
            </Grid>
            <Grid>
              <Box css={{ ...totalInstallationCss }}>
                <Typography css={{ ...subTextWithoutMargin }}>
                  {content.get('REUSABLE_COST_BEFORE_REBATES')}
                </Typography>
                <Typography css={{ ...subTextWithoutMargin }}>
                  ${toCurrencyString(costBeforeRebate, true)}
                </Typography>
              </Box>
              <Box css={{ ...totalInstallationCss }}>
                <Typography css={{ ...subTextWithoutMargin }}>
                  {content.get('REUSABLE_REBATE_AMOUNT')}
                </Typography>
                <Typography css={{ ...subTextWithoutMargin }}>
                  $
                  {lineItemRebateList && lineItemRebateList[0]?.amount
                    ? toCurrencyString(lineItemRebateList[0]?.amount, true)
                    : '0.00'}
                </Typography>
              </Box>
            </Grid>
            <hr css={{ ...hr }} />
            <Grid>
              <Box css={{ ...totalInstallationCss }}>
                <Typography css={{ ...boldText }}>
                  {content.get('REUSABLE_TOTAL_COST')}
                </Typography>
                <Typography css={{ ...boldText }}>
                  $
                  {toCurrencyString(
                    Number(totalCost) -
                      (Number(
                        lineItemRebateList && lineItemRebateList[0]?.amount,
                      ) || 0),
                    true,
                  )}
                </Typography>
              </Box>
            </Grid>
            <hr css={{ ...hr }} data-print-exclude />
            <Grid data-print-exclude>
              <Typography
                data-print-exclude
                onClick={(e: React.MouseEvent<Element, MouseEvent>) =>
                  handleDownloadWithImage(e, [
                    {
                      photo:
                        (jobImageList &&
                          jobImageList.find(
                            imgItem =>
                              imgItem.key === 'IMAGE_CHARGER_SERIAL_NO',
                          )?.imageUrl) ||
                        '',
                      title: (jobImageList && jobImageList[0].name) || '',
                      description: '',
                    },
                    {
                      photo:
                        (jobImageList &&
                          jobImageList.find(
                            imgItem =>
                              imgItem.key === 'IMAGE_CHARGER_INSTALLATION',
                          )?.imageUrl) ||
                        '',
                      title: (jobImageList && jobImageList[1].name) || '',
                      description: '',
                    },
                    {
                      photo:
                        (jobImageList &&
                          jobImageList.find(
                            imgItem => imgItem.key === 'IMAGE_CUSTOMER_INVOICE',
                          )?.imageUrl) ||
                        '',
                      title: (jobImageList && jobImageList[2].name) || '',
                      description: '',
                    },
                  ])
                }
                css={{
                  color: colors.sparkBlue,
                  fontWeight: 700,
                  fontSize: '16px !important',
                  cursor: 'pointer',
                  display: 'flex',
                  justifyContent: 'flex-end',
                }}
                role="button"
              >
                <>
                  <span
                    css={{
                      verticalAlign: 'middle',
                    }}
                  >
                    <SaveAltIcon />{' '}
                  </span>
                  {content.get('REUSABLE_DOWNLOAD')}
                </>
              </Typography>
            </Grid>
            <Box
              data-print-exclude
              mt={5}
              css={{
                display: 'flex',
                justifyContent: 'center',
                [theme.breakpoints.down('sm')]: {
                  alignItems: 'center',
                  height: '96px',
                  backgroundColor: colors.cloudGrey,
                  position: 'fixed',
                  bottom: 0,
                  width: '100%',
                  left: 0,
                  boxShadow: `0px -3px 6px ${colors.boxShadowColor}`,
                },
              }}
            >
              <InstallerButton
                color={'primary'}
                variant={'contained'}
                onClick={() =>
                  void navigate(ROUTES.PGE_PLUS_INSTALLER_PORTAL_JOB_LISTING)
                }
              >
                {content.get('REUSABLE_BACK_TO_HOME')}
              </InstallerButton>
            </Box>
          </Grid>
        </Grid>
      </Box>
    </Fragment>
  );
};

export default JobCompleted;
